/**
 * 工作流模块类型定义
 * 用于用户端展示公开的工作流应用列表
 */

// ==================== 基础类型枚举 ====================

/**
 * 工作流类型枚举
 */
export type WorkflowType = 'workflow' | 'template' | 'custom'

/**
 * 生成源类型
 */
export type GenerationSourceType = 'image_to_image' | 'text_to_image' | 'image_to_text' | 'video'

/**
 * 模块配置项
 */
export interface ModuleConfig {
  type: GenerationSourceType
  label: string
  description: string
}

/**
 * 已注册的模块配置（控制页面展示哪些模块，未上线模块注释掉即可）
 */
export const MODULE_CONFIGS: ModuleConfig[] = [
  {
    type: 'image_to_image',
    label: '图生图',
    description: '以图为基础，创作新图像',
  },
  {
    type: 'text_to_image',
    label: '文生图',
    description: '用文字描述，生成图像',
  },
  {
    type: 'image_to_text',
    label: '图生文',
    description: '从图像中提取或生成文字内容',
  },
  // { type: 'video', label: '视频', description: '...' },  // 待上线时解开注释
]

/**
 * 媒体类型枚举
 */
export type DetailMediaType = 'image' | 'video'

/**
 * 工作流状态枚举
 */
export type WorkflowStatus = 'active' | 'inactive'

/**
 * 输出类型枚举
 */
export type OutputType = 'image' | 'video' | 'audio'

// ==================== 组件类型定义 ====================

/**
 * 组件分类
 */
export const COMPONENT_CATEGORIES = {
  PROMPT: 'prompt',
  SELECT: 'select',
  BASE: 'base',
  IMAGE: 'image',
  COLOR_SELECT: 'color_select',
} as const

export type ComponentCategory =
  (typeof COMPONENT_CATEGORIES)[keyof typeof COMPONENT_CATEGORIES]

/**
 * 组件分类名称映射
 */
export const COMPONENT_CATEGORY_MAP: Record<ComponentCategory, string> = {
  [COMPONENT_CATEGORIES.PROMPT]: '提示词',
  [COMPONENT_CATEGORIES.SELECT]: '选择',
  [COMPONENT_CATEGORIES.BASE]: '基础',
  [COMPONENT_CATEGORIES.IMAGE]: '图像',
  [COMPONENT_CATEGORIES.COLOR_SELECT]: '颜色选择',
}

/**
 * 组件类型
 */
export const COMPONENT_TYPES = {
  // 提示词类
  POSITIVE_PROMPT: 'PositivePrompt',
  NEGATIVE_PROMPT: 'NegativePrompt',
  PROMPT_TEMPLATE: 'PromptTemplate',
  // 选择类
  SINGLE_SELECT_CHIPS: 'SingleSelectChips',
  MULTI_SELECT_CHIPS: 'MultiSelectChips',
  CUSTOM_SELECT_TEXT: 'CustomSelectText',
  CUSTOM_SELECT_NUMBER: 'CustomSelectNumber',
  LORA_SELECT: 'LoraSelect',
  MODEL_SELECT: 'ModelSelect',
  // 基础类
  RANDOM_SEED: 'RandomSeed',
  CONTROL_SWITCH: 'ControlSwitch',
  RATIO_SELECT: 'RatioSelect',
  REDRAW_STRENGTH: 'RedrawStrength',
  NUMBER_SLIDER: 'NumberSlider',
  CUSTOM_NUMBER: 'CustomNumber',
  CUSTOM_TEXT: 'CustomText',
  CUSTOM_TIP: 'CustomTip',
  // 图像类
  SINGLE_IMAGE_UPLOAD: 'SingleImageUpload',
  MULTI_IMAGE_UPLOAD: 'MultiImageUpload',
  IMAGE_SELECT: 'ImageSelect',
  MASK_EDITOR: 'MaskEditor',
  // 颜色选择类
  COLOR_PICKER: 'ColorPicker',
} as const

export type ComponentType =
  (typeof COMPONENT_TYPES)[keyof typeof COMPONENT_TYPES]

/**
 * 组件类型名称映射
 */
export const COMPONENT_TYPE_MAP: Record<ComponentType, string> = {
  // 提示词类
  [COMPONENT_TYPES.POSITIVE_PROMPT]: '正向提示词',
  [COMPONENT_TYPES.NEGATIVE_PROMPT]: '负向提示词',
  [COMPONENT_TYPES.PROMPT_TEMPLATE]: '提示词模板',
  // 选择类
  [COMPONENT_TYPES.SINGLE_SELECT_CHIPS]: '标签单选',
  [COMPONENT_TYPES.MULTI_SELECT_CHIPS]: '标签多选',
  [COMPONENT_TYPES.CUSTOM_SELECT_TEXT]: '下拉选择-文本',
  [COMPONENT_TYPES.CUSTOM_SELECT_NUMBER]: '下拉选择-数字',
  [COMPONENT_TYPES.LORA_SELECT]: 'Lora选择',
  [COMPONENT_TYPES.MODEL_SELECT]: '模型选择',
  // 基础类
  [COMPONENT_TYPES.RANDOM_SEED]: '随机种子',
  [COMPONENT_TYPES.CONTROL_SWITCH]: '控制开关',
  [COMPONENT_TYPES.RATIO_SELECT]: '比例选择',
  [COMPONENT_TYPES.REDRAW_STRENGTH]: '重绘幅度',
  [COMPONENT_TYPES.NUMBER_SLIDER]: '数字滑块',
  [COMPONENT_TYPES.CUSTOM_NUMBER]: '数字输入',
  [COMPONENT_TYPES.CUSTOM_TEXT]: '文本输入',
  [COMPONENT_TYPES.CUSTOM_TIP]: '提示信息',
  // 图像类
  [COMPONENT_TYPES.SINGLE_IMAGE_UPLOAD]: '上传图片',
  [COMPONENT_TYPES.MULTI_IMAGE_UPLOAD]: '批量上传',
  [COMPONENT_TYPES.IMAGE_SELECT]: '图片选择',
  [COMPONENT_TYPES.MASK_EDITOR]: '遮罩编辑',
  // 颜色选择类
  [COMPONENT_TYPES.COLOR_PICKER]: '颜色选择',
}

// ==================== 组件配置接口 ====================

/**
 * 组件选项（用于选择类组件）
 */
export interface ComponentOption {
  label: string
  value: string | number
  billing_weight?: number
}

/**
 * 组件标识
 */
export interface ComponentFlags {
  count?: boolean
  duration?: boolean
  resolution?: boolean
  ratio?: boolean
}

/**
 * 滑块配置
 */
export interface SliderConfig {
  min?: number
  max?: number
  step?: number
}

/**
 * 开关配置
 */
export interface SwitchConfig {
  onValue?: number | boolean
  offValue?: number | boolean
}

/**
 * 表单组件配置
 */
export interface FormComponent {
  id: string
  component_type: ComponentType
  component_category: ComponentCategory
  title: string
  param_name: string
  node_id: string | null
  node_field: string | null
  is_required: boolean
  default_value: unknown
  options: ComponentOption[] | null
  config: SliderConfig | SwitchConfig | Record<string, unknown> | null
  flags: ComponentFlags | null
  sort_order: number
}

/**
 * 产出节点配置
 */
export interface OutputNode {
  id: string
  node_id: string
  node_field: string
  output_type: OutputType
  sort_order: number
}

/**
 * 应用代表作
 */
export interface Showcase {
  id: string
  image_url: string
  sort_order: number
}

/**
 * 分类信息
 */
export interface WorkflowCategory {
  id: string
  name: string
}

/**
 * 工作流实体（公开列表展示用，简化版）
 */
export interface PublicWorkflow {
  /** 唯一标识 */
  id: string
  /** 工作流名称 */
  name: string
  /** 工作流标题 */
  title: string
  /** 描述 */
  description: string
  /** 封面图 URL */
  cover_url: string
  /** 图标 URL */
  icon_url: string
  /** 详情媒体类型 */
  detail_media_type: DetailMediaType
  /** 详情媒体 URL */
  detail_media_url: string
  /** 工作流类型 */
  workflow_type: WorkflowType
  /** 生成源类型 */
  generation_source_type: GenerationSourceType
  /** 消耗算力 */
  power_consumption: number
  /** 价格 */
  price: number
  /** 排序权重 */
  sort_order: number
  /** 标签 */
  tags: string[]
  /** 代表作 */
  showcases: Showcase[]
  /** 分类 ID */
  category_id: string | null
  /** 分类信息（可能由后端关联返回） */
  category?: WorkflowCategory | null
  /** 是否启用多组提示词 */
  enable_multi_prompt?: boolean
  /** 创建时间 */
  created_at: string
  /** 更新时间 */
  updated_at: string
}

/**
 * 工作流分类选项
 */
export interface WorkflowCategoryOption {
  id: string
  name: string
}

/**
 * 工作流列表查询参数
 */
export interface WorkflowListParams {
  /** 页码 */
  page?: number
  /** 每页数量 */
  pageSize?: number
  /** 分类 ID */
  categoryId?: string
  /** 关键词搜索 */
  keyword?: string
}

/**
 * 工作流列表响应数据
 */
export interface WorkflowListData {
  list: PublicWorkflow[]
  total: number
  page: number
  page_size: number
}

/**
 * 分类列表响应数据
 */
export interface CategoryListData {
  list: WorkflowCategoryOption[]
}

/**
 * API 响应包装结构
 */
export interface WorkflowApiResponse<T> {
  code: number
  message: string
  data: T
  trace_id?: string
}

// ==================== 工作流详情类型 ====================

/**
 * 工作流详情（用于详情页，包含完整表单配置）
 */
export interface WorkflowDetail extends PublicWorkflow {
  /** MCP 描述 */
  mcp_description: string
  /** 超时时间（秒） */
  timeout_seconds: number
  /** 是否禁用取消 */
  is_cancel_disabled: boolean
  /** 是否公开 */
  is_public: boolean
  /** 是否启用多组提示词 */
  enable_multi_prompt: boolean
  /** 表单组件列表 */
  form_components: FormComponent[]
  /** 输出节点配置 */
  output_nodes: OutputNode[]
}

// ==================== 表单工具函数 ====================

/**
 * 表单状态类型
 */
export type FormState = Record<string, unknown>

/**
 * 表单错误类型
 */
export type FormErrors = Record<string, string>

/**
 * 按分类分组的表单组件
 */
export type GroupedComponents = Partial<Record<ComponentCategory, FormComponent[]>>

/**
 * 表单默认值初始化选项
 */
export interface InitializeFormStateOptions {
  /** 是否限制共享用户选择高倍率配置 */
  restrictHighCostOptions?: boolean
}

/**
 * 将表单组件按分类分组
 * @param components - 表单组件列表
 * @returns 按分类分组的组件
 */
export function groupComponentsByCategory(
  components: FormComponent[]
): GroupedComponents {
  const grouped: GroupedComponents = {}

  // 按分类分组
  components.forEach((component) => {
    const category = component.component_category
    grouped[category] ??= []
    grouped[category].push(component)
  })

  // 按 sort_order 排序每个分类
  Object.values(grouped).forEach((categoryComponents) => {
    categoryComponents.sort((a, b) => a.sort_order - b.sort_order)
  })

  return grouped
}

/**
 * 标准化默认值类型，使其与选项值类型匹配
 * @param defaultValue - 原始默认值
 * @param options - 组件选项列表
 * @returns 类型匹配后的默认值
 */
function normalizeDefaultValue(
  defaultValue: unknown,
  options: ComponentOption[] | null | undefined
): unknown {
  // 如果没有选项或默认值为空，直接返回
  if (!options || options.length === 0 || defaultValue === null || defaultValue === undefined) {
    return defaultValue
  }

  // 获取第一个选项的值类型作为参考
  const firstOption = options[0]
  if (firstOption === undefined) {
    return defaultValue
  }
  const firstOptionValue = firstOption.value
  const targetType = typeof firstOptionValue

  // 如果默认值类型已经匹配，直接返回
  if (typeof defaultValue === targetType) {
    return defaultValue
  }

  // 如果目标类型是 number 且默认值是 string，尝试转换
  if (targetType === 'number' && typeof defaultValue === 'string') {
    const parsed = Number(defaultValue)
    // 检查转换后的值是否在选项中存在
    if (!isNaN(parsed) && options.some((opt) => opt.value === parsed)) {
      return parsed
    }
  }

  // 如果目标类型是 string 且默认值是 number，尝试转换
  if (targetType === 'string' && typeof defaultValue === 'number') {
    const converted = String(defaultValue)
    // 检查转换后的值是否在选项中存在
    if (options.some((opt) => opt.value === converted)) {
      return converted
    }
  }

  return defaultValue
}

/**
 * 判断共享用户是否允许选择当前选项
 * @param option - 组件选项
 * @returns 是否允许共享用户选择
 */
export function isSharedUserOptionAvailable(option: ComponentOption): boolean {
  return option.billing_weight === undefined || option.billing_weight <= 1
}

/**
 * 获取共享用户可用的默认值
 * @param normalizedValue - 已标准化的默认值
 * @param options - 组件选项列表
 * @param restrictHighCostOptions - 是否限制高倍率选项
 * @returns 共享用户可用的默认值
 */
function getAccessibleDefaultValue(
  normalizedValue: unknown,
  options: ComponentOption[] | null | undefined,
  restrictHighCostOptions: boolean
): unknown {
  if (!restrictHighCostOptions || !options || options.length === 0) {
    return normalizedValue
  }

  if (typeof normalizedValue !== 'string' && typeof normalizedValue !== 'number') {
    return normalizedValue
  }

  const selectedOption = options.find((option) => option.value === normalizedValue)
  if (selectedOption !== undefined && isSharedUserOptionAvailable(selectedOption)) {
    return normalizedValue
  }

  const fallbackOption = options.find(isSharedUserOptionAvailable)
  return fallbackOption?.value ?? normalizedValue
}

/**
 * 初始化表单默认值
 * @param components - 表单组件列表
 * @param options - 默认值初始化选项
 * @returns 表单默认值
 */
export function initializeFormState(
  components: FormComponent[],
  options: InitializeFormStateOptions = {}
): FormState {
  const state: FormState = {}

  components.forEach((component) => {
    // 对于有选项的组件，标准化默认值类型以匹配选项值类型
    const normalizedValue = normalizeDefaultValue(
      component.default_value,
      component.options
    )
    state[component.param_name] = getAccessibleDefaultValue(
      normalizedValue,
      component.options,
      options.restrictHighCostOptions ?? false
    )
  })

  return state
}

/**
 * 判断表单当前是否包含共享用户不可用的高倍率配置
 * @param components - 表单组件列表
 * @param formState - 表单状态
 * @returns 是否存在不可用配置
 */
export function hasRestrictedHighCostSelections(
  components: FormComponent[],
  formState: FormState
): boolean {
  return components.some((component) => {
    const options = component.options ?? []

    if (options.length === 0) {
      return false
    }

    const selectedValue = formState[component.param_name]
    const selectedOption = options.find((option) => option.value === selectedValue)

    return selectedOption !== undefined && !isSharedUserOptionAvailable(selectedOption)
  })
}

/**
 * 计算总费用
 * @param basePower - 基础算力消耗
 * @param components - 表单组件列表
 * @param formState - 表单状态
 * @param imageCount - 图片数量
 * @param promptCount - 提示词数量
 * @returns 总费用
 */
export function calculateTotalCost(
  basePower: number,
  components: FormComponent[],
  formState: FormState,
  imageCount = 1,
  promptCount = 1
): number {
  let multiplier = 1.0

  components.forEach((component) => {
    if (component.options && component.options.length > 0) {
      const selectedValue = formState[component.param_name]
      const selectedOption = component.options.find(
        (opt) => opt.value === selectedValue
      )
      if (selectedOption?.billing_weight !== undefined) {
        multiplier *= selectedOption.billing_weight
      }
    }
  })

  return basePower * multiplier * imageCount * promptCount
}
